import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { CheckCircle, Warning, X } from '@phosphor-icons/react';
import { createMember } from '../api/members';
import { listMyOrganizations } from '../api/orgs';
import { ApiError } from '../api/client';
import {
  validatePersonId,
  type PersonIdResult,
} from '../lib/personid';
import { isValidEmail } from '../lib/contact';
import { PhoneInput } from './ui/phone-input';

export interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  // Called when the user clicks "Open existing person" on a 409
  // personal_id_exists error — the parent surfaces the existing member's
  // MemberCard side panel and closes this modal. Optional; when omitted
  // the action button is hidden and the user sees only the inline message.
  onOpenExisting?: (memberID: string) => void;
}

// AddMemberModal uses the native <dialog> element for built-in focus trap,
// escape-to-close, and modal backdrop semantics. Modern browsers all
// support it; no library / no React-portal-rolled-our-own dance.

export function AddMemberModal({ open, onClose, slug, onOpenExisting }: AddMemberModalProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [personalId, setPersonalId] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Specific reason from the server's personid validator. Lets us render a
  // more useful error than the generic invalid_input.
  const [pidServerReason, setPidServerReason] = useState<string | null>(null);
  // Identity of the existing member when the server returns 409
  // personal_id_exists — drives the "Open existing person" affordance so
  // the user can jump straight to the record they were about to duplicate.
  const [existingMember, setExistingMember] = useState<{ id: string; name: string } | null>(null);
  // Live phone validity from PhoneInput's libphonenumber parse — warn-only,
  // never blocks submit. Defaults true so a blank field isn't flagged.
  const [phoneValid, setPhoneValid] = useState(true);

  // Email gate: invalid only when non-blank and not a valid bare address. The
  // server requires a valid email on create; surface it early. Phone stays
  // warn-only (free-text by design).
  const emailInvalid = email.trim() !== '' && !isValidEmail(email);
  const phoneWarn = phone.trim() !== '' && !phoneValid;

  // Country for the org. Read from the cached my-orgs query (DashboardShell
  // populates it) so the modal doesn't need its own /me/organizations fetch.
  // Empty string when not yet loaded — disables client-side validation but
  // never blocks submit; the server runs the authoritative check anyway.
  const token = auth.user?.access_token ?? null;
  const { data: orgs } = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const countryCode = useMemo(
    () => orgs?.find((o) => o.slug === slug)?.country_code ?? '',
    [orgs, slug],
  );

  // Live validation — green check / red error inline as the user types.
  // Only runs when there's both a country and a non-trivial input; an empty
  // field is "neutral" (no decoration).
  const pidResult: PersonIdResult | null = useMemo(() => {
    const trimmed = personalId.trim();
    if (!trimmed || !countryCode) return null;
    return validatePersonId(countryCode, trimmed);
  }, [personalId, countryCode]);

  // Open / close the native <dialog>. Reset form state on every open so a
  // previous half-filled draft doesn't leak into a new attempt.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setStreetAddress('');
      setPostalCode('');
      setCity('');
      setPersonalId('');
      setErrorCode(null);
      setPidServerReason(null);
      setExistingMember(null);
      setPhoneValid(true);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // Treat the user pressing Esc / clicking the backdrop as a close.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(streetAddress.trim() ? { street_address: streetAddress.trim() } : {}),
        ...(postalCode.trim() ? { postal_code: postalCode.trim() } : {}),
        ...(city.trim() ? { city: city.trim() } : {}),
        // Send the canonical form when the client validator accepted it,
        // raw (trimmed) otherwise — the server is the authoritative check
        // and will reject invalid values with a structured 400.
        ...(personalId.trim()
          ? { personal_id: pidResult?.valid ? pidResult.canonical : personalId.trim() }
          : {}),
      };
      return createMember(token, slug, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      onClose();
    },
    onError: (err) => {
      // Structured "invalid_personal_id" response carries a specific reason
      // (invalid_format / invalid_checksum / invalid_date) so we can show
      // copy that points at the right fix.
      if (err instanceof ApiError && err.code === 'invalid_personal_id') {
        const reason = (err.data as { reason?: string } | null)?.reason ?? 'invalid_format';
        setPidServerReason(reason);
        setErrorCode('invalid_personal_id');
        return;
      }
      // "personal_id_exists" carries the existing member's id + name so we
      // can offer a one-click jump to their record instead of leaving the
      // user to find them by searching. Empty `existing_member_id` is the
      // race-path: the dedup fired at the DB layer (concurrent insert) and
      // the surrounding tx was poisoned, so the server couldn't enrich.
      // We fall back to the anonymous message + no jump button.
      if (err instanceof ApiError && err.code === 'personal_id_exists') {
        const data = err.data as { existing_member_id?: string; existing_name?: string } | null;
        if (data?.existing_member_id) {
          setExistingMember({
            id: data.existing_member_id,
            name: data.existing_name?.trim() ?? '',
          });
        }
        setErrorCode('personal_id_exists');
        return;
      }
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    setPidServerReason(null);
    setExistingMember(null);
    if (!firstName.trim() || !lastName.trim()) {
      setErrorCode('invalid_input');
      return;
    }
    // Email is required + format-checked by the server on create; block the
    // round-trip when the client already knows it's malformed (the inline
    // message under the field explains it). Phone is warn-only, never blocks.
    if (emailInvalid) return;
    // Block submit when the client validator says "no" — same UX as before
    // for required fields. Empty input is fine (PID is optional).
    if (personalId.trim() && pidResult && !pidResult.valid) {
      setErrorCode('invalid_personal_id');
      setPidServerReason(pidResult.reason);
      return;
    }
    mutation.mutate();
  };

  return (
    <dialog ref={dialogRef} className="modal">
      <form className="modal__inner" onSubmit={onSubmit} noValidate>
        <header className="modal__header">
          <h2>{t('members.addModal.title')}</h2>
          <button
            type="button"
            className="modal__close"
            aria-label={t('common.cancel')}
            onClick={onClose}
          >
            <X size={20} aria-hidden />
          </button>
        </header>

        <div className="modal__body form">
          <div className="form__row">
            <div className="form__field">
              <label htmlFor="member-first-name">{t('members.column.firstName')}</label>
              <input
                id="member-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={120}
                autoFocus
                autoComplete="given-name"
              />
            </div>
            <div className="form__field">
              <label htmlFor="member-last-name">{t('members.column.lastName')}</label>
              <input
                id="member-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                maxLength={120}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__field">
              <label htmlFor="member-email">{t('members.column.email')}</label>
              <input
                id="member-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={254}
                autoComplete="email"
                aria-invalid={emailInvalid || undefined}
              />
              {emailInvalid && (
                <p className="form__hint" style={{ color: 'var(--color-error)' }} role="alert">
                  {t('members.form.invalidEmail')}
                </p>
              )}
            </div>
            <div className="form__field">
              <label htmlFor="member-phone">{t('members.column.phone')}</label>
              <PhoneInput
                id="member-phone"
                value={phone}
                defaultCountry={(countryCode || 'se').toLowerCase()}
                aria-label={t('members.column.phone')}
                onChange={setPhone}
                onValidityChange={setPhoneValid}
              />
              {phoneWarn && (
                <p className="form__hint">{t('members.form.invalidPhoneWarning')}</p>
              )}
            </div>
          </div>

          <div className="form__field">
            <label htmlFor="member-street">{t('members.column.streetAddress')}</label>
            <input
              id="member-street"
              type="text"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              maxLength={200}
              autoComplete="street-address"
            />
          </div>

          <div className="form__row">
            <div className="form__field">
              <label htmlFor="member-postal">{t('members.column.postalCode')}</label>
              <input
                id="member-postal"
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                maxLength={20}
                autoComplete="postal-code"
              />
            </div>
            <div className="form__field">
              <label htmlFor="member-city">{t('members.column.city')}</label>
              <input
                id="member-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={120}
                autoComplete="address-level2"
              />
            </div>
          </div>

          <div className="form__field">
            <label htmlFor="member-personal-id">
              {t('members.personalId.label')}
            </label>
            <input
              id="member-personal-id"
              type="text"
              value={personalId}
              onChange={(e) => {
                setPersonalId(e.target.value);
                setPidServerReason(null);
              }}
              maxLength={32}
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={t(`members.personalId.placeholder.${countryCode || 'default'}`, {
                defaultValue: t('members.personalId.placeholder.default'),
              })}
              aria-invalid={pidResult ? !pidResult.valid : undefined}
              aria-describedby="member-personal-id-feedback"
            />
            {/* Live feedback row — only renders when the user has typed
                something AND we know the country. Green check when valid
                (also shows the canonical full-year form so they can see we
                expanded "80" → "1980"); red warning otherwise. */}
            {pidResult && (
              <p
                id="member-personal-id-feedback"
                className="form__hint"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: pidResult.valid ? 'var(--color-success, #2d7a2d)' : 'var(--color-error)',
                  marginTop: '0.25rem',
                }}
              >
                {pidResult.valid ? (
                  <>
                    <CheckCircle size={16} aria-hidden weight="fill" />
                    <span>
                      {t('members.personalId.canonical', { value: pidResult.canonical })}
                    </span>
                  </>
                ) : (
                  <>
                    <Warning size={16} aria-hidden />
                    <span>
                      {t(`members.personalId.reason.${pidResult.reason}`, {
                        defaultValue: t('members.personalId.reason.invalid_format'),
                      })}
                    </span>
                  </>
                )}
              </p>
            )}
            {!pidResult && (
              <p className="form__hint">{t('members.personalId.hint')}</p>
            )}
          </div>

          {errorCode && (
            <div role="alert" className="error">
              {errorCode === 'invalid_personal_id' && pidServerReason ? (
                <p>
                  {t(`members.personalId.reason.${pidServerReason}`, {
                    defaultValue: t('members.personalId.reason.invalid_format'),
                  })}
                </p>
              ) : errorCode === 'personal_id_exists' ? (
                <>
                  <p>
                    {existingMember?.name
                      ? t('members.addModal.error.personal_id_exists', { name: existingMember.name })
                      : t('members.addModal.error.personal_id_exists_anonymous')}
                  </p>
                  {existingMember?.id && onOpenExisting && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        onOpenExisting(existingMember.id);
                        onClose();
                      }}
                    >
                      {t('members.addModal.error.personal_id_exists_action')}
                    </button>
                  )}
                </>
              ) : (
                <p>
                  {t(`members.addModal.error.${errorCode}`, {
                    defaultValue: t('members.addModal.error.unknown'),
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={mutation.isPending || !firstName.trim() || !lastName.trim() || emailInvalid}
          >
            {mutation.isPending ? t('common.submitting') : t('members.addModal.submit')}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
